import React, { useState } from "react";
import { Calendar, ChevronLeft, ArrowLeft } from "lucide-react";
import Sidebar from "../../components/Sidebar";
import DeclineKYCModal from "../../components/ui/DeclinKycModal";
import {
  useKycById,
  useUserById,
  useUpdateKycById,
  useApproveKycById,
} from "../../services/queries/queries";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";

const KYCRequestForm = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: kycData,
    isLoading: kycLoading,
    error: kycError,
  } = useKycById(id);
  const {
    data: userData,
    isError: userError,
    isLoading: userLoading,
  } = useUserById(kycData?.data?.user?.uuid);
  const updateKycMutation = useUpdateKycById();
  const approveKyc = useApproveKycById();




  const openDocument = (url) => {
    window.open(url, "_blank");
  };

  const user = userData?.data;
  const kyc = kycData?.data;
  if (kycLoading || userLoading) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex justify-center items-center flex-1">
          <p className="text-gray-600">Loading user data...</p>
        </div>
      </div>
    );
  }

  if (kycError || userError) {
    return (
      <div className="flex overflow-hidden flex-col h-screen md:flex-row">
        {/* Sidebar handles its own desktop/mobile rendering */}
        <Sidebar />
        
        {/* Main content — scrolls independently */}
        <div className="flex justify-center items-center flex-1">
          <p className="text-red-600">
            Error: {kycError?.message || userError?.message}
          </p>
        </div>
      </div>
    );
  }

  const handleApprove = async () => {
    try {
      const promise = approveKyc.mutateAsync({
        id,
      });

      await toast.promise(promise, {
        loading: "Approving KYC...",
        success: "KYC approved successfully",
        error: "Failed to approve KYC",
      });
    } catch (error) {
      console.error("Error approving KYC:", error);
    }
  };

  const handleDecline = () => {
    setIsModalOpen(true);
  };

  const handleSubmitDecline = async (reason) => {
    try {
      // `id` here is the KYC record's own id (this page is routed by
      // /kyc-requests/:id and fetches via useKycById(id)) - the decline
      // endpoint needs the *user's* numeric id, which lives on kyc.user.id,
      // not the KYC record's id itself.
      const promise = updateKycMutation.mutateAsync({
        data: {
          userId: kyc?.user?.id,
          declineReason: reason,
        },
      });

      await toast.promise(promise, {
        loading: "Declining KYC...",
        success: "KYC declined successfully",
        error: "Failed to decline KYC",
      });
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error declining KYC:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  return (
    <div className="flex overflow-hidden flex-col h-screen md:flex-row">
      {/* Sidebar handles its own desktop/mobile rendering */}
      <Sidebar />
      
      {/* Main content — scrolls independently */}
      <div className="overflow-y-auto flex-1 p-4 py-7 mb-9 bg-white md:py-7">
        <div className="px-8 mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div className="space-y-1">
              <h1 className="text-2xl font-semibold py-2">KYC Requests</h1>
              <button
                className="flex items-center text-[#013941]"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-6 h-4 mr-1 text-[#013941]" />
                Back
              </button>
            </div>
            <div className="space-x-3">
              <button
                className="px-6 py-2 bg-emerald-800 text-white rounded-full font-sans text-base font-bold"
                onClick={handleApprove}
                disabled={updateKycMutation.isLoading}
              >
                {updateKycMutation.isLoading ? "Approving..." : "Approve"}
              </button>
              <button
                className="px-6 py-2 border border-[#EB5757] text-[#EB5757] rounded-full font-sans"
                onClick={handleDecline}
                disabled={updateKycMutation.isLoading}
              >
                Decline
              </button>
              <DeclineKYCModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmitDecline}
              />
            </div>
          </div>

          <div className="space-y-6 border border-[#CCF6E5] rounded-3xl px-8 py-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  First name
                </label>
                <input
                  type="text"
                  value={user?.firstName || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Last name
                </label>
                <input
                  type="text"
                  value={user?.lastName || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                  readOnly
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={user?.email || ""}
                  className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                  readOnly
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Phone number
                </label>
                <div className="flex">
                  <select className="p-2 border rounded-l-full border-r-0 bg-gray-50 text-[#000E10] text-sm font-medium outline-none">
                    <option>+234</option>
                  </select>
                  <input
                    type="text"
                    value={user?.phoneNumber?.replace("+234", "") || ""}
                    className="outline-none font-sans flex-1 p-2 border rounded-r-full text-[#000E10] text-sm font-medium"
                    readOnly
                  />
                </div>
              </div>
            </div>

            <div className="max-w-xs">
              <label className="block text-sm text-gray-600 mb-1">
                Date of birth
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formatDate(user?.dob) || ""}
                  className="outline-none w-full p-2 pl-10 border rounded-full text-[#000E10] text-sm font-medium"
                  readOnly
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#013941]" />
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Identification
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm text-gray-600">
                      Means of identification
                    </label>
                    {!kyc?.documentType && (
                      <button className="flex items-center gap-2 text-[#013941]">
                        <span className="text-sm font-semibold mr-5">
                          View document
                        </span>
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={kyc?.documentType || "Not provided"}
                      className="w-full outline-none p-2 pr-24 border rounded-full font-sans text-[#828282]"
                      readOnly
                    />
                    {kyc?.documentType && (
                      <button
                        className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-sm"
                        onClick={() => openDocument(kyc?.document)}
                      >
                        view document
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    {kyc?.dataVerificationType}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={kyc?.dataVerificationValue || "Not provided"}
                      className="w-full outline-none p-2 pr-24 border rounded-full text-[#000E10] text-sm font-medium py-3"
                      readOnly
                    />
                    {kyc?.idNo && (
                      <button className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1 bg-emerald-100 text-emerald-800 rounded-lg text-sm">
                        Verified
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="font-medium text-[#828282] text-base py-3">
                Address
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Street address
                  </label>
                  <input
                    type="text"
                    value={kyc?.address || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Town/City
                  </label>
                  <input
                    type="text"
                    value={kyc?.city || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={kyc?.state || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={kyc?.country || user?.country || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium"
                    readOnly
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Postal code
                  </label>
                  <input
                    type="text"
                    value={kyc?.postalCode || ""}
                    className="outline-none font-sans w-full p-2 border rounded-full text-[#000E10] text-sm font-medium py-2"
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KYCRequestForm;
